import {
  gql,
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'

import {
  CreateTradeRequestMutation,
  CreateTradeRequestMutationVariables,
  ItemDetailSelectQuery,
  ItemDetailSelectQueryVariables,
  SerialNumberSelectQuery,
  SerialNumberSelectQueryVariables,
  TradeBoxFragmentDoc,
  TradeItemDetailSelectItemDetailBoxFragmentDoc,
  UserInfoFragmentDoc,
  UserItemBoxFragmentDoc,
  UserTradableUserItemsConnectionFragmentDoc,
  UserTradableUserItemsQuery,
  UserTradableUserItemsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ userId: string; itemId: string }>()
  const { analytics } = useAnalytics()
  const { data, loading } = useQuery<
    SerialNumberSelectQuery,
    SerialNumberSelectQueryVariables
  >(QUERY, { variables: { ...router.query } })

  const [
    getUserTradableUserItems,
    {
      data: userItems,
      fetchMore: fetchMoreUserTradableUserItems,
      networkStatus,
    },
  ] = useLazyQuery<
    UserTradableUserItemsQuery,
    UserTradableUserItemsQueryVariables
  >(GET_USER_TRADABLE_USER_ITEMS, {
    onCompleted({ userTradableUserItems }) {
      if (!userTradableUserItems.edges.length) {
        analytics?.track('impression_noData', {
          applicationKey: config.applicationKey,
          itemId: router.query.itemId,
          name: 'providableItems',
        })
      }
    },
  })

  const [
    getItemInformation,
    { data: itemDetailSelect, loading: itemInformationLoading },
  ] = useLazyQuery<ItemDetailSelectQuery, ItemDetailSelectQueryVariables>(
    GET_ITEM_DETAIL_SELECT,
    { returnPartialData: true }
  )

  const [createTradeRequest] = useMutation<
    CreateTradeRequestMutation,
    CreateTradeRequestMutationVariables
  >(CREATE_TRADE_REQUEST)

  return {
    state: {
      serialNumberSelect: {
        portfolio: data?.portfolio ?? null,
        loading,
      },
      userTradableUserItems: {
        loading: networkStatus === NetworkStatus.loading,
        fetching: networkStatus === NetworkStatus.fetchMore,
        collections: { edges: [] },
        hasNextPage: !!userItems?.userTradableUserItems?.pageInfo?.hasNextPage,
        items: userItems?.userTradableUserItems ?? null,
      },
      tradeItemDetailSelect: {
        loading: itemInformationLoading,
        portfolio: itemDetailSelect?.portfolio ?? null,
      },
    },
    actions: {
      async onTradeConfirm() {},
      async onGetUserTradableUserItems() {
        await getUserTradableUserItems({
          variables: { pageInput: { limit: 12 } },
        })
      },
      async onFetchMoreUserTradableUserItems() {
        if (!fetchMoreUserTradableUserItems) {
          return
        }
        await fetchMoreUserTradableUserItems({
          variables: {
            pageInput: {
              limit: 12,
              cursor: userItems?.userTradableUserItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onGetTradeItemDetailSelect(itemId) {
        await getItemInformation({
          variables: { id: itemId },
        })
      },
      async onCreateRequestTrade({ requestComment, ...values }) {
        if (requestComment?.content) {
          return await createTradeRequest({
            variables: { inputs: { ...values, requestComment } },
          }).then(res => {
            analytics?.track('click_sendTradeRequest', {
              applicationKey: config.applicationKey,
              itemId: router.query.itemId,
              withMessage: true,
            })
            return res.data?.createTradeRequest.id ?? ''
          })
        }
        return await createTradeRequest({ variables: { inputs: values } }).then(
          res => {
            analytics?.track('click_sendTradeRequest', {
              applicationKey: config.applicationKey,
              itemId: router.query.itemId,
              withMessage: false,
            })
            return res.data?.createTradeRequest.id ?? ''
          }
        )
      },
    },
  }
})

const QUERY = gql`
  query SerialNumberSelect($itemId: ID!, $userId: String, $skuId: String) {
    portfolio(id: $itemId, userId: $userId) {
      ...UserItemBox
    }
  }
  ${UserItemBoxFragmentDoc}
`

const GET_USER_TRADABLE_USER_ITEMS = gql`
  query UserTradableUserItems($pageInput: PageInput) {
    userTradableUserItems(pageInput: $pageInput) {
      ...UserTradableUserItemsConnection
    }
  }
  ${UserTradableUserItemsConnectionFragmentDoc}
`

const GET_ITEM_DETAIL_SELECT = gql`
  query ItemDetailSelect($id: ID!, $userId: String, $skuId: String) {
    portfolio(id: $id, userId: $userId) {
      id
      number
      type
      createdAt
      isSystemProtected
      isUserProtected
      item {
        ...TradeItemDetailSelectItemDetailBox
      }
      serials {
        id
        isProtected
        serialId
        isUsed
        itemId
        obtainedDate
        formattedObtainedDate @client
        formattedSerialNumber
      }
      user {
        ...UserInfo
      }
    }
  }
  ${TradeItemDetailSelectItemDetailBoxFragmentDoc}
  ${UserInfoFragmentDoc}
`

const CREATE_TRADE_REQUEST = gql`
  mutation CreateTradeRequest($inputs: CreateTradeRequestInput!) {
    createTradeRequest(inputs: $inputs) {
      ...TradeBox
    }
  }
  ${TradeBoxFragmentDoc}
`
